import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Chip,
    LinearProgress,
    IconButton,
    Grid
} from '@mui/joy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosConfig from '../util/axiosConfig';

const Attendance = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modulesSummary, setModulesSummary] = useState([]);
    const [expandedModule, setExpandedModule] = useState(null);
    const [loadingModule, setLoadingModule] = useState(null);
    const [moduleDetails, setModuleDetails] = useState({});

    useEffect(() => {
        fetchAttendanceSummary();
    }, []);

    const fetchAttendanceSummary = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/attendance/summary');
            setModulesSummary(response.data.modules || []);
        } catch (err) {
            console.error('Error fetching attendance summary:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load attendance data');
        } finally {
            setLoading(false);
        }
    };

    const handleCardClick = (module) => {
        // If clicking on the same module that's expanded, collapse it
        if (expandedModule === module) {
            setExpandedModule(null);
            return;
        }

        // If clicking on a different module, expand it (previous one will automatically collapse)
        // Check if details are already loaded
        if (moduleDetails[module]) {
            setExpandedModule(module);
            return;
        }

        // Expand immediately and fetch details (will show spinner while loading)
        setExpandedModule(module);
        fetchModuleDetails(module);
    };

    const fetchModuleDetails = async (module) => {
        try {
            setLoadingModule(module);
            const response = await axiosConfig.get(`/students/attendance/${encodeURIComponent(module)}`);
            setModuleDetails(prev => ({
                ...prev,
                [module]: response.data
            }));
        } catch (err) {
            console.error('Error fetching module details:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load attendance details');
        } finally {
            setLoadingModule(null);
        }
    };

    const getPercentageColor = (percentage) => {
        if (percentage >= 75) return 'success';
        if (percentage >= 50) return 'warning';
        return 'danger';
    };

    const getProgressColor = (percentage) => {
        if (percentage >= 75) return '#28a745';
        if (percentage >= 50) return '#ffc107';
        return '#dc3545';
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading attendance summary...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (modulesSummary.length === 0) {
        return (
            <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        No attendance records found
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <EventAvailableIcon sx={{ color: '#eb7a24', fontSize: '2rem' }} />
                <Typography level="h2" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                    Attendance
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                {modulesSummary.map((moduleSummary, index) => {
                    const isExpanded = expandedModule === moduleSummary.module;
                    const details = moduleDetails[moduleSummary.module];
                    const isLoadingDetails = loadingModule === moduleSummary.module;

                    return (
                        <Box key={moduleSummary.module || index} sx={{ flex: '1 1 300px', minWidth: '300px', maxWidth: '100%' }}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: 'auto',
                                    minWidth: '300px',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: 'md',
                                        transform: 'translateY(-2px)'
                                    },
                                    borderColor: isExpanded ? '#eb7a24' : 'divider'
                                }}
                                onClick={() => handleCardClick(moduleSummary.module)}
                            >
                                <CardContent sx={{ py: 1.5 }}>
                                    {/* First row: Module name, percentage chip, and expand icon */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                            <Typography level="h4" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                {moduleSummary.module}
                                            </Typography>
                                            <Chip
                                                color={getPercentageColor(moduleSummary.percentage)}
                                                variant="soft"
                                                size="sm"
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                {moduleSummary.percentage}%
                                            </Chip>
                                        </Box>
                                        <IconButton
                                            variant="plain"
                                            size="sm"
                                            sx={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                            }}
                                        >
                                            <ExpandMoreIcon />
                                        </IconButton>
                                    </Box>

                                    {/* Second row: Progress bar with counts and percentage at intersection */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {/* Present count on left */}
                                        <Typography level="body-xs" sx={{ color: '#28a745', fontWeight: 'bold', minWidth: '30px', textAlign: 'right' }}>
                                            {moduleSummary.presentDays}
                                        </Typography>

                                        {/* Progress bar container with padding to prevent overlap */}
                                        <Box sx={{ flex: 1, position: 'relative', height: '24px', px: '20px' }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    height: '100%',
                                                    borderRadius: '5px',
                                                    overflow: 'visible',
                                                    position: 'relative'
                                                }}
                                            >
                                                {/* Green portion (present) */}
                                                <Box
                                                    sx={{
                                                        backgroundColor: '#28a745',
                                                        width: `${moduleSummary.percentage}%`,
                                                        transition: 'width 0.3s ease',
                                                        position: 'relative',
                                                        overflow: moduleSummary.percentage > 90 ? 'hidden' : 'visible',
                                                        borderTopLeftRadius: '5px',
                                                        borderBottomLeftRadius: '5px',
                                                        borderTopRightRadius: moduleSummary.percentage >= 100 ? '5px' : '0',
                                                        borderBottomRightRadius: moduleSummary.percentage >= 100 ? '5px' : '0'
                                                    }}
                                                >
                                                    {/* Percentage text - positioned inside green bar, ensuring it stays within bounds */}
                                                    {moduleSummary.percentage >= 15 && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                // For very high percentages (>97%), position well inside green to ensure it doesn't overflow
                                                                // For high percentages (90-97%), position near edge but inset
                                                                // For lower percentages (15-90%), position at intersection with red
                                                                right: moduleSummary.percentage > 97 ? '25px' :
                                                                    (moduleSummary.percentage > 90 ? '12px' : '2px'),
                                                                transform: moduleSummary.percentage > 97 ? 'translate(0, -50%)' :
                                                                    (moduleSummary.percentage > 90 ? 'translate(0, -50%)' : 'translate(50%, -50%)'),
                                                                backgroundColor: 'white',
                                                                px: 0.5,
                                                                borderRadius: 'xs',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 'bold',
                                                                color: '#333',
                                                                zIndex: 2,
                                                                whiteSpace: 'nowrap',
                                                                pointerEvents: 'none',
                                                                // Ensure it doesn't overflow green bar
                                                                maxWidth: moduleSummary.percentage > 97 ? 'calc(100% - 30px)' :
                                                                    (moduleSummary.percentage > 90 ? 'calc(100% - 15px)' : 'calc(100% - 4px)'),
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                        >
                                                            {moduleSummary.percentage}%
                                                        </Box>
                                                    )}
                                                </Box>
                                                {/* Red portion (absent) */}
                                                <Box
                                                    sx={{
                                                        backgroundColor: '#dc3545',
                                                        width: `${100 - moduleSummary.percentage}%`,
                                                        transition: 'width 0.3s ease',
                                                        position: 'relative',
                                                        borderTopRightRadius: '5px',
                                                        borderBottomRightRadius: '5px',
                                                        borderTopLeftRadius: moduleSummary.percentage <= 0 ? '5px' : '0',
                                                        borderBottomLeftRadius: moduleSummary.percentage <= 0 ? '5px' : '0'
                                                    }}
                                                >
                                                    {/* Percentage text - positioned at left edge of red when percentage is very low */}
                                                    {moduleSummary.percentage < 15 && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: 0,
                                                                transform: 'translate(-50%, -50%)',
                                                                backgroundColor: 'white',
                                                                px: 0.5,
                                                                borderRadius: 'xs',
                                                                fontSize: '0.7rem',
                                                                fontWeight: 'bold',
                                                                color: '#333',
                                                                zIndex: 2,
                                                                whiteSpace: 'nowrap',
                                                                pointerEvents: 'none'
                                                            }}
                                                        >
                                                            {moduleSummary.percentage}%
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Absent count on right */}
                                        <Typography level="body-xs" sx={{ color: '#dc3545', fontWeight: 'bold', minWidth: '30px', textAlign: 'left' }}>
                                            {moduleSummary.absentDays}
                                        </Typography>
                                    </Box>

                                    {/* Total days at bottom */}
                                    <Typography level="body-xs" sx={{ color: '#6e7072', mt: 1, textAlign: 'center' }}>
                                        Total Days: {moduleSummary.totalDays}
                                    </Typography>

                                    {/* Animated expand/collapse for details (smooth without layout jerk) */}
                                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Box
                                            sx={{
                                                maxHeight: isExpanded ? '1000px' : 0,
                                                overflow: 'hidden',
                                                opacity: isExpanded ? 1 : 0,
                                                transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                willChange: 'max-height',
                                                mt: 2,
                                                pt: 2
                                            }}
                                        >
                                            <Box>
                                                {isLoadingDetails ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                        <CircularProgress size="sm" />
                                                    </Box>
                                                ) : details && details.attendance && details.attendance.length > 0 ? (
                                                    <Sheet
                                                        variant="outlined"
                                                        sx={{
                                                            width: '100%',
                                                            borderRadius: 'sm',
                                                            overflow: 'auto',
                                                            maxHeight: '400px',
                                                            border: '1px solid',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <Table
                                                            aria-label={`attendance details for ${details.module}`}
                                                            sx={{
                                                                '& tbody tr:nth-child(even)': {
                                                                    backgroundColor: '#f9f9f9'
                                                                },
                                                                '& tbody tr:hover': {
                                                                    backgroundColor: '#fff5ed'
                                                                },
                                                                '& thead th': {
                                                                    backgroundColor: '#eb7a24',
                                                                    color: 'white',
                                                                    fontWeight: 'bold',
                                                                    borderBottom: '2px solid',
                                                                    borderBottomColor: '#e55125'
                                                                },
                                                                '& td, & th': {
                                                                    padding: '8px',
                                                                    border: '1px solid',
                                                                    borderColor: 'divider',
                                                                    fontSize: '0.875rem'
                                                                }
                                                            }}
                                                        >
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '130px' }}>Date</th>
                                                                    {details.hasPermission && (
                                                                        <th style={{ width: '100px' }}>Batch</th>
                                                                    )}
                                                                    <th style={{ width: '80px' }}>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {details.attendance.map((record, idx) => (
                                                                    <tr key={idx}>
                                                                        <td>
                                                                            <Typography level="body-sm">
                                                                                {record.date || '-'}
                                                                            </Typography>
                                                                        </td>
                                                                        {details.hasPermission && (
                                                                            <td>
                                                                                <Typography level="body-sm">
                                                                                    {record.batchcode || '-'}
                                                                                </Typography>
                                                                            </td>
                                                                        )}
                                                                        <td>
                                                                            {record.status === 'P' ? (
                                                                                <Chip
                                                                                    color="success"
                                                                                    size="sm"
                                                                                    variant="solid"
                                                                                >
                                                                                    P
                                                                                </Chip>
                                                                            ) : (
                                                                                <Chip
                                                                                    sx={{
                                                                                        backgroundColor: '#ff4444',
                                                                                        color: 'white'
                                                                                    }}
                                                                                    size="sm"
                                                                                    variant="solid"
                                                                                >
                                                                                    A
                                                                                </Chip>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </Sheet>
                                                ) : (
                                                    <Typography level="body-sm" sx={{ color: '#6e7072', textAlign: 'center', py: 2 }}>
                                                        No attendance records found
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default Attendance;
